/**
 * Biometric Authentication Utility
 * Uses Web Authentication API (WebAuthn) for Face ID, Touch ID, Windows Hello
 */

// Storage keys for credential management
const CREDENTIAL_STORAGE_KEY = 'biometric_credential_id';
const USER_HANDLE_STORAGE_KEY = 'biometric_user_handle';

/**
 * Check if WebAuthn is supported in the current browser
 */
export const isWebAuthnSupported = (): boolean => {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function'
  );
};

/**
 * Check if platform authenticator (built-in biometrics) is available
 * This includes Face ID, Touch ID, Windows Hello, etc.
 */
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (error) {
    console.warn('Error checking platform authenticator:', error);
    return false;
  }
};

/**
 * Check if user has enrolled biometric credentials
 */
export const hasEnrolledCredential = (): boolean => {
  const credentialId = localStorage.getItem(CREDENTIAL_STORAGE_KEY);
  return !!credentialId;
};

/**
 * Get stored credential ID
 */
export const getStoredCredentialId = (): string | null => {
  return localStorage.getItem(CREDENTIAL_STORAGE_KEY);
};

/**
 * Generate a random buffer for challenges and user handles
 */
const generateRandomBuffer = (length: number): Uint8Array<ArrayBuffer> => {
  const buffer = new Uint8Array(length);
  crypto.getRandomValues(buffer);
  return buffer;
};

/**
 * Convert ArrayBuffer to Base64 URL string
 */
const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Convert Base64 URL string to ArrayBuffer
 */
const base64UrlToBuffer = (base64Url: string): ArrayBuffer => {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Biometric credential registration options
 */
export interface BiometricRegistrationOptions {
  userId: string;
  userName: string;
  userDisplayName: string;
  rpId?: string; // Relying Party ID (domain)
  rpName?: string; // Relying Party Name
}

/**
 * Registration result
 */
export interface BiometricRegistrationResult {
  success: boolean;
  credentialId?: string;
  error?: string;
}

/**
 * Authentication result
 */
export interface BiometricAuthResult {
  success: boolean;
  credentialId?: string;
  signature?: string;
  error?: string;
}

/**
 * Register a new biometric credential (enrollment)
 * This prompts the user to use their fingerprint/face to create a credential
 */
export const registerBiometricCredential = async (
  options: BiometricRegistrationOptions
): Promise<BiometricRegistrationResult> => {
  if (!isWebAuthnSupported()) {
    return { success: false, error: 'WebAuthn is not supported in this browser' };
  }

  const platformAvailable = await isPlatformAuthenticatorAvailable();
  if (!platformAvailable) {
    return { success: false, error: 'No biometric authenticator available on this device' };
  }

  try {
    // Generate user handle (random identifier)
    const userHandle = generateRandomBuffer(32);

    // Generate challenge
    const challenge = generateRandomBuffer(32);

    // Determine RP ID (defaults to current hostname)
    const rpId = options.rpId || window.location.hostname;
    const rpName = options.rpName || 'ChecksAndTicks';

    // Create credential options
    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge,
      rp: {
        id: rpId,
        name: rpName,
      },
      user: {
        id: userHandle,
        name: options.userName,
        displayName: options.userDisplayName,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },   // ES256 (ECDSA w/ SHA-256)
        { alg: -257, type: 'public-key' }, // RS256 (RSASSA-PKCS1-v1_5 w/ SHA-256)
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform', // Built-in authenticator only
        userVerification: 'required',        // Biometric verification required
        residentKey: 'preferred',            // Discoverable credential preferred
      },
      timeout: 60000, // 60 seconds
      attestation: 'none', // We don't need attestation for local auth
    };

    // Create the credential
    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return { success: false, error: 'Failed to create credential' };
    }

    // Store credential ID and user handle locally
    const credentialId = bufferToBase64Url(credential.rawId);
    localStorage.setItem(CREDENTIAL_STORAGE_KEY, credentialId);
    localStorage.setItem(USER_HANDLE_STORAGE_KEY, bufferToBase64Url(userHandle.buffer));

    console.info('Biometric credential registered successfully');

    return { success: true, credentialId };
  } catch (error) {
    console.error('Biometric registration error:', error);

    // Handle specific error types
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Biometric authentication was cancelled or denied' };
      }
      if (error.name === 'InvalidStateError') {
        return { success: false, error: 'A credential already exists for this user' };
      }
      if (error.name === 'NotSupportedError') {
        return { success: false, error: 'This device does not support the required authentication method' };
      }
    }

    return { success: false, error: 'Failed to register biometric credential' };
  }
};

/**
 * Authenticate using stored biometric credential
 * This prompts the user to verify their identity with biometrics
 */
export const authenticateWithBiometric = async (): Promise<BiometricAuthResult> => {
  if (!isWebAuthnSupported()) {
    return { success: false, error: 'WebAuthn is not supported in this browser' };
  }

  const credentialId = getStoredCredentialId();
  if (!credentialId) {
    return { success: false, error: 'No biometric credential enrolled' };
  }

  try {
    // Generate challenge
    const challenge = generateRandomBuffer(32);

    // Create authentication options
    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge,
      allowCredentials: [
        {
          id: base64UrlToBuffer(credentialId),
          type: 'public-key',
          transports: ['internal'], // Platform authenticator
        },
      ],
      userVerification: 'required',
      timeout: 60000, // 60 seconds
    };

    // Authenticate
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions,
    }) as PublicKeyCredential;

    if (!assertion) {
      return { success: false, error: 'Authentication failed' };
    }

    // Get the authenticator assertion response
    const response = assertion.response as AuthenticatorAssertionResponse;
    const signature = bufferToBase64Url(response.signature);

    console.info('Biometric authentication successful');

    return {
      success: true,
      credentialId: bufferToBase64Url(assertion.rawId),
      signature,
    };
  } catch (error) {
    console.error('Biometric authentication error:', error);

    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Authentication was cancelled or denied' };
      }
      if (error.name === 'InvalidStateError') {
        return { success: false, error: 'Credential not found' };
      }
    }

    return { success: false, error: 'Biometric authentication failed' };
  }
};

/**
 * Remove stored biometric credential (unenroll)
 */
export const removeBiometricCredential = (): void => {
  localStorage.removeItem(CREDENTIAL_STORAGE_KEY);
  localStorage.removeItem(USER_HANDLE_STORAGE_KEY);
  console.info('Biometric credential removed');
};

/**
 * Get biometric support status summary
 */
export interface BiometricSupportStatus {
  webAuthnSupported: boolean;
  platformAuthenticatorAvailable: boolean;
  hasEnrolledCredential: boolean;
  canUseBiometrics: boolean;
}

export const getBiometricSupportStatus = async (): Promise<BiometricSupportStatus> => {
  const webAuthnSupported = isWebAuthnSupported();
  const platformAuthenticatorAvailable = webAuthnSupported
    ? await isPlatformAuthenticatorAvailable()
    : false;
  const enrolled = hasEnrolledCredential();

  return {
    webAuthnSupported,
    platformAuthenticatorAvailable,
    hasEnrolledCredential: enrolled,
    canUseBiometrics: webAuthnSupported && platformAuthenticatorAvailable,
  };
};

/**
 * Get user-friendly description of biometric type available
 */
export const getBiometricTypeDescription = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'Face ID or Touch ID';
  }
  if (/mac/.test(userAgent)) {
    return 'Touch ID';
  }
  if (/android/.test(userAgent)) {
    return 'Fingerprint or Face Unlock';
  }
  if (/windows/.test(userAgent)) {
    return 'Windows Hello';
  }

  return 'Biometric Authentication';
};
